import { memo } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/utils";

const COMPONENTS: Components = {
  p: ({ node: _node, ...props }) => <p className="my-2 first:mt-0 last:mb-0" {...props} />,
  a: ({ node: _node, href, ...props }) => {
    // react-markdown empties an unsafe link's href (javascript:, data: and the like): its text only.
    if (!href) return <span {...props} />;
    // A footnote and its way back stay on this page.
    if (href.startsWith("#"))
      return <a {...props} href={href} className="text-primary underline underline-offset-2" />;
    return (
      <a
        {...props}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2"
      />
    );
  },
  ul: ({ node: _node, ...props }) => <ul className="my-2 list-disc space-y-1 pl-5" {...props} />,
  ol: ({ node: _node, ...props }) => <ol className="my-2 list-decimal space-y-1 pl-5" {...props} />,
  h1: ({ node: _node, ...props }) => <h3 className="mb-1 mt-4 text-lg font-semibold" {...props} />,
  h2: ({ node: _node, ...props }) => (
    <h3 className="mb-1 mt-3 text-base font-semibold" {...props} />
  ),
  h3: ({ node: _node, ...props }) => <h4 className="mb-1 mt-3 text-sm font-semibold" {...props} />,
  blockquote: ({ node: _node, ...props }) => (
    <blockquote className="my-2 border-l-2 pl-3 text-muted-foreground" {...props} />
  ),
  hr: () => <hr className="my-3 border-border" />,
  // Wide tables and code scroll in their own box and never size what holds them: without
  // contain, a flex item around the chat (the app shell's main column) grows to the table's width.
  table: ({ node: _node, ...props }) => (
    <div data-agent-scroll-box className="my-2 overflow-x-auto [contain:inline-size]">
      <table className="w-max min-w-full border-collapse text-left text-[13px]" {...props} />
    </div>
  ),
  th: ({ node: _node, ...props }) => (
    <th className="border-b px-2 py-1.5 font-medium text-muted-foreground" {...props} />
  ),
  td: ({ node: _node, ...props }) => (
    <td className="border-b px-2 py-1.5 tabular-nums" {...props} />
  ),
  pre: ({ node: _node, ...props }) => (
    <pre
      data-agent-scroll-box
      className="my-2 overflow-x-auto rounded-md bg-muted p-3 text-xs [contain:inline-size]"
      {...props}
    />
  ),
  code: ({ node: _node, className, ...props }) => (
    <code className={cn("rounded bg-muted px-1 py-0.5 text-[0.9em]", className)} {...props} />
  ),
};

/**
 * An answer's text as Markdown (GitHub flavour). No raw HTML and no images (nothing loads from
 * elsewhere). Links open in a new tab, a footnote's on this page; an unsafe link is its text only.
 * While it streams, a caret blinks after the last block.
 */
export const MarkdownText = memo(function MarkdownText({
  text,
  streaming = false,
}: {
  text: string;
  streaming?: boolean;
}) {
  return (
    <div
      data-agent-markdown
      className={cn(
        "break-words text-sm leading-relaxed [&_pre_code]:bg-transparent [&_pre_code]:p-0",
        streaming &&
          "[&>:last-child]:after:ml-0.5 [&>:last-child]:after:inline-block [&>:last-child]:after:h-[1em] [&>:last-child]:after:w-px [&>:last-child]:after:translate-y-[0.15em] [&>:last-child]:after:bg-primary [&>:last-child]:after:content-[''] motion-safe:[&>:last-child]:after:animate-caret-blink"
      )}
    >
      <Markdown remarkPlugins={[remarkGfm]} components={COMPONENTS} disallowedElements={["img"]}>
        {text}
      </Markdown>
    </div>
  );
});
